import React, { useMemo, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FieldSuggestDropdown, SUGGESTABLE_FIELD_KEYS, useFieldSearchSuggestions } from './FieldSearchSuggest';
import {
  ENROLLMENT_CRITERIA_FIELDS,
  MISSING_CRITERION,
  criteriaDisplayValue,
  criteriaFieldLabel,
  isMissingCriterion,
} from '@/pages/Teaching/enrollmentSearchCriteria';

function CriterionValueInput({
  fieldKey,
  value,
  onChange,
  catalogs,
  autoFocus,
  onSubmit,
}) {
  const field = ENROLLMENT_CRITERIA_FIELDS.find((f) => f.key === fieldKey);
  const [focused, setFocused] = useState(false);
  const canSuggest = SUGGESTABLE_FIELD_KEYS.has(fieldKey);
  const { items, loading } = useFieldSearchSuggestions(fieldKey, value, catalogs, focused && canSuggest);

  if (field?.type === 'select' && field.options?.length) {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        className={`h-8 min-w-[160px] flex-1 rounded-md border px-2 text-sm ${value ? 'border-brand-red/70 bg-rose-50' : 'border-slate-200 bg-white'}`}
        data-testid="criteria-value-select"
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          e.stopPropagation();
          onSubmit?.();
        }}
      >
        <option value="">Wählen…</option>
        {field.options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }

  return (
    <div className="relative flex-1 min-w-[160px]">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        autoFocus={autoFocus}
        placeholder={field?.placeholder || 'Wert…'}
        className="w-full h-8 rounded-md border border-slate-300 px-2 text-sm"
        data-testid="criteria-value-input"
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          e.stopPropagation();
          onSubmit?.();
        }}
      />
      {focused && canSuggest && (
        <FieldSuggestDropdown
          items={items}
          loading={loading}
          highlight={0}
          onSelect={(item) => {
            onChange(item?.value ?? '');
            setFocused(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Schrittweise Kriterien-Suche für Belegungen (UND-verknüpft).
 */
export default function EnrollmentCriteriaPanel({
  criteria = {},
  onCriteriaChange,
  onSearch,
  onClear,
  onCancel,
  catalogs,
  searchActive = false,
  resultCount = 0,
  resultUnit = 'Belegung(en)',
  compact = false,
}) {
  const [draftField, setDraftField] = useState(ENROLLMENT_CRITERIA_FIELDS[0].key);
  const [draftValue, setDraftValue] = useState('');
  const [showAddRow, setShowAddRow] = useState(!searchActive);

  const activeEntries = useMemo(
    () => Object.entries(criteria).filter(([, v]) => v != null && String(v).trim()),
    [criteria],
  );

  const usedKeys = useMemo(() => new Set(activeEntries.map(([k]) => k)), [activeEntries]);

  const availableFields = ENROLLMENT_CRITERIA_FIELDS.filter((f) => !usedKeys.has(f.key) || f.key === draftField);

  const storeCriterion = (raw, { search = false } = {}) => {
    const trimmed = String(raw || '').trim();
    if (!showAddRow || !trimmed) return criteria;
    const stored = isMissingCriterion(trimmed) ? MISSING_CRITERION : trimmed;
    const next = { ...criteria, [draftField]: stored };
    onCriteriaChange(next);
    setDraftValue('');
    const following = ENROLLMENT_CRITERIA_FIELDS.find((f) => !usedKeys.has(f.key) && f.key !== draftField);
    setDraftField(following?.key || ENROLLMENT_CRITERIA_FIELDS[0].key);
    setShowAddRow(false);
    // Der Chip allein ändert die Trefferliste nicht. Jedes übernommene Kriterium sucht sofort.
    if (search) onSearch?.(next);
    return next;
  };

  const commitDraft = () => storeCriterion(draftValue);

  const addCriterion = () => {
    storeCriterion(draftValue, { search: true });
  };

  const runSearch = () => {
    onSearch(commitDraft());
  };

  const removeCriterion = (key) => {
    const next = { ...criteria };
    delete next[key];
    onCriteriaChange(next);
    const stillSearching = Object.values(next).some((v) => v != null && String(v).trim());
    if (stillSearching) onSearch?.(next);
    else onClear?.();
  };

  if (compact && !showAddRow && activeEntries.length === 0) return null;

  return (
    <div
      className="bg-slate-100 border-b border-slate-200 px-4 py-2"
      data-testid="enrollment-criteria-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="text-xs font-medium text-slate-800">
            {searchActive ? (
              <>Gefilterte Suche — <strong>{resultCount}</strong> {resultUnit}</>
            ) : (
              <>Schrittweise Suche: Kriterien nacheinander hinzufügen — alle Kriterien werden <strong>UND</strong>-verknüpft.</>
            )}
          </div>

          {activeEntries.length > 0 && (
            <ol className="flex flex-wrap gap-2 list-none p-0 m-0" data-testid="criteria-chip-list">
              {activeEntries.map(([key, value], index) => (
                <li
                  key={key}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs shadow-sm"
                  data-testid={`criteria-chip-${key}`}
                >
                  <span className="text-slate-400 font-medium">{index + 1}.</span>
                  <span className="font-semibold text-slate-700">{criteriaFieldLabel(key)}</span>
                  <span className="text-slate-500">{isMissingCriterion(value) ? 'ist leer' : 'ist'}</span>
                  {!isMissingCriterion(value) && (
                    <span className="text-slate-800">„{criteriaDisplayValue(key, value)}“</span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeCriterion(key)}
                    className="ml-0.5 text-slate-400 hover:text-red-600"
                    title="Kriterium entfernen"
                    data-testid={`criteria-remove-${key}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ol>
          )}

          {showAddRow && (
            <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="criteria-add-row">
              <span className="text-xs text-slate-500 shrink-0">
                {activeEntries.length === 0 ? 'Kriterium 1' : `Kriterium ${activeEntries.length + 1}`}:
              </span>
              <select
                value={draftField}
                onChange={(e) => {
                  setDraftField(e.target.value);
                  setDraftValue('');
                }}
                className="h-8 min-w-[180px] rounded-md border border-slate-300 px-2 text-sm"
                data-testid="criteria-field-select"
              >
                {availableFields.map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
              <CriterionValueInput
                fieldKey={draftField}
                value={draftValue}
                onChange={setDraftValue}
                catalogs={catalogs}
                autoFocus
                onSubmit={addCriterion}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => storeCriterion(MISSING_CRITERION, { search: true })}
                data-testid="criteria-empty-btn"
              >
                ist leer
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={addCriterion}
                disabled={!String(draftValue || '').trim()}
                data-testid="criteria-add-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Übernehmen
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {!showAddRow && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setShowAddRow(true)}
              data-testid="criteria-add-toggle"
            >
              <Plus className="w-3 h-3 mr-1" />
              Kriterium hinzufügen
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs bg-brand-red hover:bg-brand-red/90 text-white"
            onClick={runSearch}
            data-testid="criteria-search-run"
          >
            <Search className="w-3 h-3 mr-1" />
            Suchen
          </Button>
          {(searchActive || activeEntries.length > 0) && (
            <button
              type="button"
              onClick={onClear}
              data-testid="criteria-search-clear"
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-slate-300 bg-white text-xs font-semibold text-slate-800 hover:border-brand-red hover:text-brand-red"
            >
              <X className="w-3.5 h-3.5" />
              Alle Filter zurücksetzen
            </button>
          )}
          {!searchActive && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={onCancel}
              data-testid="criteria-search-cancel"
            >
              Abbrechen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
